import gql from 'graphql-tag';
import {
    WORLD_MAP_FRAGMENT,
    WORLD_MAP_MARKER_FRAGMENT,
    WORLD_MAP_NO_MARKERS_FRAGMENT,
    WORLD_MAP_SET_FRAGMENT,
    WORLD_MARKER_FRAGMENT
} from 'graphql/fragment/worldFragment';

export const CREATE_WORLD_MAP_MUTATION = gql`
    mutation CreateWorldMap($worldMapId: String!, $map: WorldMapInput!) {
        createWorldMap(worldMapId: $worldMapId, map: $map) {
            map {
                ...FWorldMap
            }
            token
        }
    }
    ${WORLD_MAP_FRAGMENT}
`;

export const CLONE_WORLD_MAP_MUTATION = gql`
    mutation CloneWorldMap($worldMapId: String!, $token: String) {
        cloneWorldMap(worldMapId: $worldMapId, token: $token) {
            map {
                ...FWorldMap
            }
            token
        }
    }
    ${WORLD_MAP_FRAGMENT}
`;

export const UPDATE_WORLD_MAP_MUTATION = gql`
    mutation UpdateWorldMap($worldMapId: String!, $token: String, $map: WorldMapInput!) {
        updateWorldMap(worldMapId: $worldMapId, token: $token, map: $map) {
            ...FWorldMapNoMarkers
            guideId
            guide {
                guideId
                title
            }
            timeCreated
            timeUpdated
            worldMapSets {
                title
                type
                imageUrl
                color
                worldMapSetId
                isVisibleOnLoad
                orderIndex
                markers {
                    worldMapMarkerId
                }
            }
        }
    }
    ${WORLD_MAP_NO_MARKERS_FRAGMENT}
`;

export const DELETE_WORLD_MAP_MUTATION = gql`
    mutation DeleteWorldMap($worldMapId: String!, $token: String) {
        deleteWorldMap(worldMapId: $worldMapId, token: $token) {
            success
        }
    }
`;

export const REMOVE_WORLD_MAP_MARKER_MUTATION = gql`
    mutation RemoveWorldMapMarker(
        $worldMapId: String!
        $worldMapMarkerId: String!
        $token: String
    ) {
        removeWorldMapMarker(
            worldMapId: $worldMapId
            worldMapMarkerId: $worldMapMarkerId
            token: $token
        ) {
            success
        }
    }
`;

export const LINK_WORLD_MAP_TO_USER_MUTATION = gql`
    mutation LinkWorldMapToUser($worldMapId: String!, $token: String!, $userId: String!) {
        linkWorldMapToUser(worldMapId: $worldMapId, token: $token, userId: $userId) {
            success
        }
    }
`;

export const ADD_OR_UPDATE_WORLD_MAP_MARKER = gql`
    mutation AddOrUpdateWorldMapMarker(
        $worldMapId: String!
        $worldMapMarkerId: String!
        $marker: WorldMapMarkerInput
        $token: String
    ) {
        addOrUpdateWorldMapMarker(
            worldMapId: $worldMapId
            worldMapMarkerId: $worldMapMarkerId
            marker: $marker
            token: $token
        ) {
            ...FWorldMapMarker
        }
    }

    ${WORLD_MAP_MARKER_FRAGMENT}
`;

export const CLONE_WORLD_MAP_MARKER = gql`
    mutation CloneWorldMapMarker(
        $worldMapMarkerId: String!
        $newWorldMapMarkerId: String!
        $markerDataOverride: WorldMapMarkerInput
        $cloneStoryType: ECloneWorldMarkerStoryCloneType
        $token: String
    ) {
        cloneWorldMarker(
            worldMapMarkerId: $worldMapMarkerId
            newWorldMapMarkerId: $newWorldMapMarkerId
            markerDataOverride: $markerDataOverride
            cloneStoryType: $cloneStoryType
            token: $token
        ) {
            ...FWorldMapMarker
            ...FWorldMarker
            worldMapSets {
                worldMapSetId
                title
                ...FWorldMapSet
            }
            stories {
                storyId
            }
        }
    }

    ${WORLD_MARKER_FRAGMENT}
    ${WORLD_MAP_MARKER_FRAGMENT}
    ${WORLD_MAP_SET_FRAGMENT}
`;

export const CREATE_WORLD_MAP_SET = gql`
    mutation CreateWorldMapSet(
        $worldMapId: String!
        $worldMapSet: WorldMapSetInput!
        $token: String
    ) {
        createWorldMapSet(worldMapId: $worldMapId, worldMapSet: $worldMapSet, token: $token) {
            ...FWorldMapSet
        }
    }
    ${WORLD_MAP_SET_FRAGMENT}
`;

export const UPDATE_WORLD_MAP_SET = gql`
    mutation UpdateWorldMapSet(
        $worldMapSetId: String!
        $worldMapSet: WorldMapSetInput!
        $token: String
    ) {
        updateWorldMapSet(worldMapSetId: $worldMapSetId, worldMapSet: $worldMapSet, token: $token) {
            ...FWorldMapSet
        }
    }
    ${WORLD_MAP_SET_FRAGMENT}
`;

export const REMOVE_WORLD_MAP_SET = gql`
    mutation RemoveWorldMapSet($worldMapSetId: String!, $token: String) {
        removeWorldMapSet(worldMapSetId: $worldMapSetId, token: $token) {
            success
        }
    }
`;

export const ADD_TO_WORLD_MAP_SET = gql`
    mutation AddToWorldMapSet($worldMapSetId: String!, $worldMapMarkerId: String!, $token: String) {
        addToWorldMapSet(
            worldMapSetId: $worldMapSetId
            worldMapMarkerId: $worldMapMarkerId
            token: $token
        ) {
            ...FWorldMapSet
        }
    }

    ${WORLD_MAP_SET_FRAGMENT}
`;

export const REMOVE_FROM_WORLD_MAP_SET = gql`
    mutation RemoveFromWorldMapSet(
        $worldMapSetId: String!
        $worldMapMarkerId: String!
        $token: String
    ) {
        removeFromWorldMapSet(
            worldMapSetId: $worldMapSetId
            worldMapMarkerId: $worldMapMarkerId
            token: $token
        ) {
            ...FWorldMapSet
        }
    }

    ${WORLD_MAP_SET_FRAGMENT}
`;

// Used only for StartupExtreme stand map
export const REUPLOAD_IMAGE_FROM_URL = gql`
    mutation ReuploadImageFromUrl($imageUrl: String!, $resolutions: [Float!]!) {
        reuploadImage(imageUrl: $imageUrl, resolutions: $resolutions) {
            imageUrl
            resolution
        }
    }
`;

export const REORDER_MARKERS_MUTATION = gql`
    mutation ReorderWorldMarkers(
        $worldMapId: String!
        $order: [MarkerOrderInput!]!
        $token: String
    ) {
        reorderWorldMarkers(worldMapId: $worldMapId, order: $order, token: $token) {
            worldMapMarkerId
            order
        }
    }
`;

export const CREATE_BOOKING_MUTATION = gql`
    mutation CreateBooking($worldMapId: String!, $worldMapMarkerId: String!, $data: JSONObject!) {
        createBooking(worldMapId: $worldMapId, worldMapMarkerId: $worldMapMarkerId, data: $data) {
            success
        }
    }
`;

export const DELETE_BOOKING_MUTATION = gql`
    mutation DeleteBooking(
        $worldMapId: String!
        $worldMapMarkerId: String!
        $worldMapBookingId: String!
    ) {
        deleteBooking(
            worldMapId: $worldMapId
            worldMapMarkerId: $worldMapMarkerId
            worldMapBookingId: $worldMapBookingId
        ) {
            success
        }
    }
`;
